import z from 'zod'

import {
  HOMEPAGE_VIEW_ANALYTICS,
  HOMEPAGE_VIEW_SCORECARD,
  HomepageDefaultsSchema,
} from './homepage'

export const OrganizationSetup = z.object({
  welcome: z.boolean().default(false),
})
export type OrganizationSetup = z.infer<typeof OrganizationSetup>

export const ScorecardSettings = z
  .object({
    recordingDuration: z
      .object({
        min: z.number().int().min(0).default(0), // seconds
        max: z.number().int().min(0).nullable().default(null), // seconds, null = no limit
      })
      .default({ min: 0, max: null }),
    messageCountMin: z.number().int().min(0).nullable().default(null), // minimum messages, null = disabled
    primaryAgentInvolvement: z.number().int().min(1).max(4).default(2), // 1=80%, 2=60%, 3=40%, 4=any
    speakerRatioLimit: z.number().int().min(50).max(100).nullable().default(null), // percentage, null = disabled
    excludeVoicemails: z.boolean().default(false), // exclude voicemails from auto-evaluation
    excludePossibleAgentAssignmentIssues: z.boolean().default(true),
  })
  .refine(
    ({ recordingDuration }) =>
      recordingDuration.max === null || recordingDuration.min <= recordingDuration.max,
    {
      path: ['recordingDuration', 'max'],
      message: 'Maximum duration must be greater than or equal to minimum duration',
    },
  )
export type ScorecardSettings = z.infer<typeof ScorecardSettings>

export const RecordingType = z.enum(['auto-detect', 'stereo', 'mono'])
export type RecordingType = z.infer<typeof RecordingType>

export const TranscriptionSettings = z.object({
  recordingType: RecordingType.default('auto-detect'),
  channelIdentification: z
    .array(z.enum(['Agent', 'Customer', 'System']))
    .default(['Customer', 'Agent', 'System']),
  autoAssignSpeakers: z.boolean().default(false),
})
export type TranscriptionSettings = z.infer<typeof TranscriptionSettings>

export const PIIRedactionSettings = z.object({
  enabled: z.boolean().default(true),
  redactSSN: z.boolean().default(true),
  redactBirthday: z.boolean().default(true),
  redactCreditCard: z.boolean().default(true),
})
export type PIIRedactionSettings = z.infer<typeof PIIRedactionSettings>

export const OrganizationSettings = z.object({
  timezone: z.string().nullable().default(null),
  scorecard: ScorecardSettings.default({
    recordingDuration: { min: 0, max: null },
    messageCountMin: null,
    primaryAgentInvolvement: 2,
    speakerRatioLimit: null,
    excludeVoicemails: false,
    excludePossibleAgentAssignmentIssues: true,
  }),
  transcription: TranscriptionSettings.default({
    recordingType: 'auto-detect',
    channelIdentification: ['Customer', 'Agent', 'System'],
    autoAssignSpeakers: false,
  }),
  homepageDefaults: HomepageDefaultsSchema.default({
    agent: HOMEPAGE_VIEW_SCORECARD,
    admin: HOMEPAGE_VIEW_ANALYTICS,
  }),
  piiRedaction: PIIRedactionSettings.default({
    enabled: true,
    redactSSN: true,
    redactBirthday: true,
    redactCreditCard: true,
  }),
})
export type OrganizationSettings = z.infer<typeof OrganizationSettings>

export const OrganizationSettingsPatch = z.object({
  timezone: z.string().nullable().optional(),
  scorecard: z
    .object({
      recordingDuration: z
        .object({
          min: z.number().int().min(0).optional(),
          max: z.number().int().min(0).nullable().optional(),
        })
        .optional(),
      messageCountMin: z.number().int().min(0).nullable().optional(),
      primaryAgentInvolvement: z.number().int().min(1).max(4).optional(),
      speakerRatioLimit: z.number().int().min(50).max(100).nullable().optional(),
      excludeVoicemails: z.boolean().optional(),
      excludePossibleAgentAssignmentIssues: z.boolean().optional(),
    })
    .optional(),
  transcription: z
    .object({
      recordingType: RecordingType.optional(),
      channelIdentification: z.array(z.enum(['Agent', 'Customer', 'System'])).optional(),
      autoAssignSpeakers: z.boolean().optional(),
    })
    .optional(),
  homepageDefaults: z
    .object({
      agent: HomepageDefaultsSchema.shape.agent.removeDefault().optional(),
      admin: HomepageDefaultsSchema.shape.admin.removeDefault().optional(),
    })
    .optional(),
  piiRedaction: z
    .object({
      enabled: z.boolean().optional(),
      redactSSN: z.boolean().optional(),
      redactBirthday: z.boolean().optional(),
      redactCreditCard: z.boolean().optional(),
    })
    .optional(),
})
export type OrganizationSettingsPatch = z.infer<typeof OrganizationSettingsPatch>

export const MAX_FLAGS_PER_ORG = 20

export const OrganizationMutationSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters long')
    .max(60, 'Name must be less than 60 characters'),
  alias: z
    .string()
    .min(2, 'URL must be at least 2 characters long')
    .max(60, 'URL must be less than 60 characters')
    .regex(/^[a-z0-9-]+$/, 'URL can only contain lowercase letters, numbers, and dashes'),
  setup: z
    .object({
      welcome: z.boolean(),
    })
    .partial()
    .optional(),
  iconFileId: z.string().nullish(),
  settings: OrganizationSettingsPatch.optional(),
})
